import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    openLocalDatabaseForVerifiedTenant,
    closeLocalTenantDatabase,
    upgradeLegacyLocalDataForVerifiedTenant,
} from '../services/db';
import {
    passwordRotationStateFromClaims,
    passwordRotationStateFromIdentity,
} from '../utils/session-boot';
import { clearTokens as clearAuthTokens } from '../services/data-service-api';
import { setDemoAllowedRecipients, setDemoBusiness } from '../services/demo-business';
import { setSessionQuoteNumberPrefix } from '../services/business-quote-prefix';
import { isServerUnavailableError, subscribeBackendReachability } from '../services/network-resilience';
import { restoreBrandAccent } from '../services/brand-accent';

export interface UseTenantSessionVerificationOptions {
    user: any;
    setUser: React.Dispatch<React.SetStateAction<any>>;
    enforceVerifiedLocalTenant: boolean;
    setLocalTenantSessionVerified: React.Dispatch<React.SetStateAction<boolean>>;
    teardownAuthenticatedLocalSession: (opts?: any) => Promise<any>;
    passwordRotationBootState?: string;
    /** Test seam. Production uses IDENTITY_RETRY_DELAYS_MS. */
    identityRetryDelaysMs?: readonly number[];
}

/**
 * The identity check could not reach QuoteMaker's server. The session is kept
 * and the check runs again at `nextRetryAt` (or at once on Try now, or when
 * the reconnect loop finds the server answering).
 */
export interface SessionVerificationOutage {
    attempt: number;
    nextRetryAt: number;
    reason: string;
}

export interface TenantSessionVerificationState {
    outage: SessionVerificationOutage | null;
    retryNow: () => void;
}

export const IDENTITY_RETRY_DELAYS_MS: readonly number[] = [2000, 4000, 8000, 15000, 30000];

/**
 * Only the server saying no (401/403) means signed out. Outage lane,
 * 2026-09-29: the boot check read the load balancer's 502 during a deploy as
 * an invalid session, deleted the tokens and showed the login form, while the
 * server still held the session. A failure that means "the check could not
 * be answered" (gateway, no response, the sign-in service down, a 5xx, a
 * timeout, rate limiting) keeps the session and tries again.
 */
export function isRetryableIdentityCheckFailure(err: any): boolean {
    const status = err?.status;
    if (status === 401 || status === 403) return false;
    if (isServerUnavailableError(err)) return true;
    return typeof status === 'number' && (status >= 500 || status === 408 || status === 429);
}

export function useTenantSessionVerification({
    user,
    setUser,
    enforceVerifiedLocalTenant,
    setLocalTenantSessionVerified,
    teardownAuthenticatedLocalSession,
    passwordRotationBootState,
    identityRetryDelaysMs = IDENTITY_RETRY_DELAYS_MS,
}: UseTenantSessionVerificationOptions): TenantSessionVerificationState {
    const [outage, setOutage] = useState<SessionVerificationOutage | null>(null);
    const [retrySeq, setRetrySeq] = useState(0);
    const attemptRef = useRef(0);
    const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // True while an identity check is in flight. The check that finds the
    // server answering flips reachability itself (apiFetch marks every 2xx),
    // and that flip must not start a second check.
    const checkInFlightRef = useRef(false);
    const retryDelaysRef = useRef(identityRetryDelaysMs);
    retryDelaysRef.current = identityRetryDelaysMs;

    const retryNow = useCallback(() => {
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
        setRetrySeq((seq) => seq + 1);
    }, []);

    // The reconnect loop (network-resilience) probes /api/health on its own
    // backoff. When it finds the server answering, check again at once rather
    // than waiting out this hook's own timer.
    useEffect(() => {
        if (!outage) return undefined;
        return subscribeBackendReachability((reachable) => {
            if (reachable && !checkInFlightRef.current) retryNow();
        });
    }, [outage, retryNow]);

    useEffect(() => {
        if (!user) {
            // Nobody is signed in, so no business is a demo (DEMO-SPEC S1)
            // and no real address is allowed (migration 126).
            setDemoBusiness(false);
            setDemoAllowedRecipients([]);
            setSessionQuoteNumberPrefix('');
            // Nor any business's colour (G2). Sign-in puts it on :root
            // (services/business-profile-startup.ts) and sign-out does not
            // reload the page, so without this the sign-in screen and the
            // next business wear the last one's colour until their own
            // profile loads. The stylesheet default resumes.
            restoreBrandAccent({});
            attemptRef.current = 0;
            setOutage(null);
            if (enforceVerifiedLocalTenant) {
                setLocalTenantSessionVerified(false);
            }
            return undefined;
        }
        let cancelled = false;
        if (enforceVerifiedLocalTenant) setLocalTenantSessionVerified(false);
        checkInFlightRef.current = true;
        (async () => {
            try {
                const { apiFetch } = await import('../services/data-service-api.js');
                const me = await apiFetch('/auth/me');
                if (cancelled) return;
                if (!me || me.error) throw new Error(me?.error || 'Identity response unavailable');
                const hydratedPasswordRotationState = passwordRotationStateFromIdentity(me);
                if (hydratedPasswordRotationState === 'failed') {
                    throw new Error('Identity response omitted password-rotation state');
                }
                // tenants.is_demo is fixed when a business is added (migration
                // 125), so the answer holds for the whole session.
                // A demo business prints its own identity, never RJ Bird's
                // (services/demo-identity.ts). Stored before any surface is
                // released below. Anything but a literal true reads as false.
                setDemoBusiness(me.tenantIsDemo);
                // The real addresses a demo business may open email drafts to
                // (migration 126, services/demo-send-guard.ts). Only a demo
                // business keeps a list; the server sends [] for every other.
                setDemoAllowedRecipients(me.tenantIsDemo === true ? me.tenantDemoAllowedRecipients : []);
                // The business's quote prefix (its stored prefix, or its
                // business name's initials), so a title or Quote # made before
                // the tenant profile has loaded never borrows RJ Bird's "RBB"
                // (services/business-quote-prefix.ts). '' from an older server.
                setSessionQuoteNumberPrefix(me.tenantQuoteNumberPrefix);
                if (enforceVerifiedLocalTenant) {
                    await openLocalDatabaseForVerifiedTenant({
                        tenantId: me.tenantId,
                        userId: me.id,
                    });
                    if (cancelled) {
                        closeLocalTenantDatabase();
                        return;
                    }
                    const mayReadTenantProjects = [
                        'quotemaker', 'quick-edit', 'project-imports',
                        'analysis', 'clients',
                    ].some(areaId => me.access?.[areaId] === true);
                    if (mayReadTenantProjects) {
                        try {
                            const ownershipEnvelope = await apiFetch('/projects/local-cache-identities');
                            if (cancelled) {
                                closeLocalTenantDatabase();
                                return;
                            }
                            const upgrade = await upgradeLegacyLocalDataForVerifiedTenant(ownershipEnvelope);
                            if (upgrade.migratedProjects > 0) {
                                console.info('[QM] Recovered tenant-owned local Project data:', upgrade);
                            }
                            if (upgrade.skippedUnscopedFolderState) {
                                console.warn(
                                    '[QM] A pre-tenant folder connection remains unclaimed. Reconnect it in this workspace to establish tenant ownership.',
                                );
                            }
                        } catch (upgradeError: any) {
                            console.warn(
                                '[QM] Tenant-owned legacy local recovery skipped:',
                                upgradeError?.message || upgradeError,
                            );
                        }
                    }
                    if (cancelled) {
                        closeLocalTenantDatabase();
                        return;
                    }
                    setLocalTenantSessionVerified(true);
                }
                // G6 + G2: the business profile, read once for EVERY role that
                // can open the app, with the brand colour applied as it lands.
                // The quoting-brain warm-up used to be its only start-up reader,
                // so office admins and viewers never had it. Not before a
                // temporary password is changed: every route but /auth/me,
                // change-password and logout answers 403 until then. Not
                // awaited: start-up never waits on it.
                if (hydratedPasswordRotationState === 'clear') {
                    void import('../services/business-profile-startup')
                        .then(({ loadBusinessProfileAtStartup }) => loadBusinessProfileAtStartup())
                        .catch(() => { /* non-fatal: the next reader reads it */ });
                }
                attemptRef.current = 0;
                setOutage(null);
                setUser((prev: any) => (prev ? {
                    ...prev,
                    id: me.id ?? prev.id,
                    name: me.name ?? prev.name,
                    email: me.email ?? prev.email,
                    role: me.role ?? prev.role,
                    phone: me.phone ?? null,
                    tenantId: me.tenantId ?? prev.tenantId ?? null,
                    tenantName: me.tenantName ?? null,
                    tenantIsDemo: me.tenantIsDemo === true,
                    access: me.access ?? null,
                    areaOverrides: me.areaOverrides ?? null,
                    mustChangePassword: me.mustChangePassword === true,
                    passwordRotationBootState: hydratedPasswordRotationState,
                    canManageUsers: me.canManageUsers === true,
                } : prev));
            } catch (err: any) {
                if (cancelled) return;
                if (isRetryableIdentityCheckFailure(err)) {
                    // The server did not answer the check. Keep the tokens and
                    // the local data, show the "QuoteMaker is updating" state,
                    // and ask again on a backoff. The tenant database stays
                    // closed until the server confirms who this is.
                    const delays = retryDelaysRef.current.length > 0 ? retryDelaysRef.current : IDENTITY_RETRY_DELAYS_MS;
                    const attempt = attemptRef.current;
                    attemptRef.current = attempt + 1;
                    const delay = delays[Math.min(attempt, delays.length - 1)]!;
                    console.warn(
                        `[QM] QuoteMaker's server did not answer the sign-in check (${err?.status ?? err?.code ?? 'no response'}). `
                        + `Keeping the session and trying again in ${Math.round(delay / 1000)}s.`,
                    );
                    setOutage({
                        attempt: attempt + 1,
                        nextRetryAt: Date.now() + delay,
                        reason: String(err?.code || err?.status || 'unreachable'),
                    });
                    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
                    retryTimerRef.current = setTimeout(() => {
                        retryTimerRef.current = null;
                        setRetrySeq((seq) => seq + 1);
                    }, delay);
                    return;
                }
                console.warn(
                    '[QM] Authenticated workspace verification failed:',
                    err?.message || err,
                    {
                        code: err?.code || 'AUTHENTICATED_WORKSPACE_VERIFICATION_FAILED',
                        mismatchReason: err?.mismatchReason || 'not-classified',
                    },
                );
                attemptRef.current = 0;
                setOutage(null);
                if (enforceVerifiedLocalTenant) {
                    setLocalTenantSessionVerified(false);
                    void teardownAuthenticatedLocalSession({ tokenReason: 'invalid-session' });
                } else if (passwordRotationBootState === 'checking') {
                    try { clearAuthTokens({ reason: 'invalid-session' }); } catch { /* best-effort */ }
                    setUser((prev: any) => (
                        prev && passwordRotationStateFromClaims(prev) === 'checking'
                            ? null
                            : prev
                    ));
                }
            }
        })().finally(() => {
            if (!cancelled) checkInFlightRef.current = false;
        });
        return () => {
            cancelled = true;
            checkInFlightRef.current = false;
            if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, enforceVerifiedLocalTenant, teardownAuthenticatedLocalSession, retrySeq]);

    return { outage, retryNow };
}
